import { useNavigate, useParams } from 'react-router-dom';
import { FormationCard } from '../components/FormationCard';
import { ImgPlaceholder } from '../components/ImgPlaceholder';
import { StateMessage } from '../components/StateMessage';
import { useFormateur, useFormations } from '../data/queries';
import { useSEO } from '../hooks/useSEO';
import type { FormateurCouleur, Tone } from '../types';

const FORMATEUR_TONE: Record<FormateurCouleur, Tone> = {
  vert: 'vert',
  rouge: 'rouge',
  or: 'creme',
};

/** Make a user-pasted social/web string into a usable href. */
function normaliseLink(raw: string): string {
  const v = raw.trim();
  if (!v) return '#';
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v.replace(/^\/\//, '')}`;
}

export function FormateurDetail() {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const formateurQ = useFormateur(id);
  const formationsQ = useFormations();
  const fo = formateurQ.data;

  useSEO({
    title: fo?.nom ?? 'Formateur',
    description: fo?.bio.slice(0, 160),
    ogImage: fo?.photo,
  });

  if (formateurQ.isLoading) return <StateMessage variant="loading" />;
  if (formateurQ.error) return <StateMessage variant="error" message={String(formateurQ.error)} />;
  if (!fo) {
    return (
      <div>
        <p>Formateur introuvable.</p>
      </div>
    );
  }

  const formations = (formationsQ.data ?? []).filter((f) => fo.formations.includes(f.id));

  return (
    <div className="page-formateur">
      <button type="button" className="mtc-back" onClick={() => navigate('/formateurs')}>
        ← Tous les formateurs
      </button>
      <div className="fo-grid">
        <div className="fo-img">
          <ImgPlaceholder
            label={fo.nom}
            ratio="3/4"
            tone={FORMATEUR_TONE[fo.couleur]}
            src={fo.photo}
            alt={fo.nom}
          />
        </div>
        <div className="fo-main">
          <span className="mtc-eyebrow">{fo.ville}</span>
          <h1>{fo.nom}</h1>
          <p className="fo-titre">{fo.titre}</p>
          <p className="fo-bio">{fo.bio}</p>

          {(fo.linkedinUrl ||
            fo.instagramUrl ||
            fo.facebookUrl ||
            fo.tiktokUrl ||
            fo.email) && (
            <div className="fo-socials" aria-label={`Contacter ${fo.nom}`}>
              {fo.linkedinUrl && (
                <a
                  href={normaliseLink(fo.linkedinUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fo-social fo-social-linkedin"
                  aria-label="LinkedIn"
                  title="LinkedIn"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.44-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.48-.91 1.66-1.85 3.41-1.85 3.65 0 4.32 2.4 4.32 5.52v6.22zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.23 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.46c.98 0 1.77-.77 1.77-1.72V1.72C24 .77 23.21 0 22.23 0z"
                    />
                  </svg>
                </a>
              )}
              {fo.instagramUrl && (
                <a
                  href={normaliseLink(fo.instagramUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fo-social fo-social-instagram"
                  aria-label="Instagram"
                  title="Instagram"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.72 3.72 0 0 1-1.38-.9 3.72 3.72 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63a5.88 5.88 0 0 0-2.13 1.38A5.88 5.88 0 0 0 .63 4.14C.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91a5.88 5.88 0 0 0 1.38 2.13 5.88 5.88 0 0 0 2.13 1.38c.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.88 5.88 0 0 0 2.13-1.38 5.88 5.88 0 0 0 1.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.88 5.88 0 0 0-1.38-2.13A5.88 5.88 0 0 0 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32zm0 10.16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.4-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z"
                    />
                  </svg>
                </a>
              )}
              {fo.facebookUrl && (
                <a
                  href={normaliseLink(fo.facebookUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fo-social fo-social-facebook"
                  aria-label="Facebook"
                  title="Facebook"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M22.675 0H1.325C.593 0 0 .593 0 1.325v21.351C0 23.408.593 24 1.325 24H12.82v-9.294H9.692V11.08h3.128V8.412c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.464.099 2.796.143v3.24h-1.918c-1.504 0-1.795.715-1.795 1.763v2.31h3.59l-.467 3.626h-3.123V24h6.116c.73 0 1.323-.592 1.323-1.324V1.325C24 .593 23.408 0 22.675 0z"
                    />
                  </svg>
                </a>
              )}
              {fo.tiktokUrl && (
                <a
                  href={normaliseLink(fo.tiktokUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fo-social fo-social-tiktok"
                  aria-label="TikTok"
                  title="TikTok"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-5.86 1.7 6.13 6.13 0 0 0-2.07 4.6 6.18 6.18 0 0 0 11.61 2.92 6.34 6.34 0 0 0 .65-2.86V8.66a8.18 8.18 0 0 0 4.9 1.59V6.78a4.85 4.85 0 0 1-.01-.09z"
                    />
                  </svg>
                </a>
              )}
              {fo.email && (
                <a
                  href={`mailto:${fo.email}`}
                  className="fo-social fo-social-email"
                  aria-label="Email"
                  title={fo.email}
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <path
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 6h18v12H3z M3 6l9 7 9-7"
                    />
                  </svg>
                </a>
              )}
            </div>
          )}

          <h3>Formations animées en 2026</h3>
          <div className="cat-grid">
            {formations.map((f) => (
              <FormationCard key={f.id} f={f} onClick={() => navigate(`/formations/${f.id}`)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
